const DEFAULT_WINDOW_SIZE = 5;

export const ADMIN_PAGE_SIZE = 10;
export const PUBLIC_EVENTS_PAGE_SIZE = 12;
export const BOOKING_PAGE_SIZE = 5;
export const SKELETON_DELAY_MS = 200;

const toPositiveInt = (value, fallback) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.floor(parsed);
};

export const totalPagesFromCount = (count, pageSize) =>
  Math.max(1, Math.ceil(Math.max(0, Number(count) || 0) / toPositiveInt(pageSize, ADMIN_PAGE_SIZE)));

export const pageRange = (page, pageSize) => {
  const size = toPositiveInt(pageSize, ADMIN_PAGE_SIZE);
  const safePage = toPositiveInt(page, 1);
  const from = (safePage - 1) * size;
  return { from, to: from + size - 1 };
};

export const slicePage = (items, page, pageSize) => {
  const list = Array.isArray(items) ? items : [];
  const { from, to } = pageRange(page, pageSize);
  return list.slice(from, to + 1);
};

export const uniqueIds = (rows, key) => [
  ...new Set((Array.isArray(rows) ? rows : []).map((row) => row?.[key]).filter(Boolean)),
];

export const readPageFromSearch = (searchParams, fallback = 1) => {
  const parsed = Number(searchParams?.get?.("page"));
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return Math.floor(parsed);
};

export const applyPagedSelect = (query, page, pageSize) => {
  const { from, to } = pageRange(page, pageSize);
  return query.range(from, to);
};

/**
 * Sliding window: 1 … 12 13 14 15 16 … 50
 * Always includes first/last when total exceeds the window.
 */
export const getSlidingWindowItems = (currentPage, totalPages, windowSize = DEFAULT_WINDOW_SIZE) => {
  const safeTotal = toPositiveInt(totalPages, 1);
  const safeCurrent = Math.min(safeTotal, toPositiveInt(currentPage, 1));
  const size = toPositiveInt(windowSize, DEFAULT_WINDOW_SIZE);

  if (safeTotal <= size + 2) {
    return Array.from({ length: safeTotal }, (_, index) => ({ type: "page", page: index + 1 }));
  }

  let start = Math.max(2, safeCurrent - Math.floor(size / 2));
  let end = start + size - 1;
  if (end > safeTotal - 1) {
    end = safeTotal - 1;
    start = Math.max(2, end - size + 1);
  }

  const items = [{ type: "page", page: 1 }];
  if (start > 2) items.push({ type: "ellipsis" });
  for (let page = start; page <= end; page += 1) {
    items.push({ type: "page", page });
  }
  if (end < safeTotal - 1) items.push({ type: "ellipsis" });
  items.push({ type: "page", page: safeTotal });
  return items;
};

export const getCompactWindowItems = (currentPage, totalPages) => {
  const safeTotal = toPositiveInt(totalPages, 1);
  const safeCurrent = Math.min(safeTotal, toPositiveInt(currentPage, 1));

  if (safeTotal <= 3) {
    return Array.from({ length: safeTotal }, (_, index) => ({ type: "page", page: index + 1 }));
  }

  const items = [{ type: "page", page: 1 }];
  if (safeCurrent > 2) items.push({ type: "ellipsis" });
  if (safeCurrent !== 1 && safeCurrent !== safeTotal) {
    items.push({ type: "page", page: safeCurrent });
  }
  if (safeCurrent < safeTotal - 1) items.push({ type: "ellipsis" });
  items.push({ type: "page", page: safeTotal });
  return items;
};
export const getPageNumberGroup = (currentPage, totalPages, groupSize = DEFAULT_WINDOW_SIZE) => {
  const items = getSlidingWindowItems(currentPage, totalPages, groupSize);
  const pages = items.filter((item) => item.type === "page").map((item) => item.page);
  const first = pages[0];
  const last = pages[pages.length - 1];
  const safeTotal = toPositiveInt(totalPages, 1);
  return {
    pages,
    showLeadingEllipsis: items.some((item) => item.type === "ellipsis") && first === 1 && pages[1] > 2,
    showTrailingEllipsis: items.some((item) => item.type === "ellipsis") && last === safeTotal && pages[pages.length - 2] < safeTotal - 1,
  };
};
